import {
  MessageType, PlayerCollisionEvent, PlayerInput, GAME,
  UpgradePurchaseRequest, UpgradePurchaseResult,
} from '@push.io/shared';
import { readScopedToken, writeScopedToken } from '../session/storage.js';

export class NetworkClient {
  private ws: WebSocket | null = null;
  private url: string;
  private connected: boolean = false;
  private playerId: string | null = null;
  private playerName: string = 'Jugador';
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private messageQueue: ArrayBuffer[] = [];

  private onWelcome: ((data: any) => void) | null = null;
  private pendingWelcome: any = null;
  private onSnapshot: ((data: any) => void) | null = null;
  private onMatchStart: ((data: any) => void) | null = null;
  private onMatchEnd: ((data: any) => void) | null = null;
  private onMatchResultUpdated: ((data: any) => void) | null = null;
  private onWalletUpdated: ((data: any) => void) | null = null;
  private onMatchState: ((data: any) => void) | null = null;
  private onQueueStatus: ((data: any) => void) | null = null;
  private onError: ((error: string) => void) | null = null;
  private onPlayerCollision: ((event: PlayerCollisionEvent) => void) | null = null;
  private onUpgradeResult: ((result: UpgradePurchaseResult) => void) | null = null;

  constructor(url: string) {
    this.url = url;
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    try {
      this.ws = new WebSocket(this.url);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.sendConnect();
        this.flushQueue();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = () => {
        this.connected = false;
        this.reconnect();
      };

      this.ws.onerror = () => {
        console.error('[Network] WebSocket error');
      };
    } catch (err) {
      console.error('[Network] Connection error:', err);
      this.reconnect();
    }
  }

  private reconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Network] Max reconnection attempts reached');
      return;
    }
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    console.log(`[Network] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => this.connect(), delay);
  }

  private sendConnect(): void {
    const token = this.getOrCreateToken();
    this.send(MessageType.Connect, {
      token,
      version: GAME.GAME_VERSION,
      name: this.playerName,
      cosmetics: {
        skinId: 'default',
        trailId: 'default',
        collisionEffectId: 'default',
      },
    });
  }

  private getOrCreateToken(): string {
    let token = readScopedToken();
    if (!token) {
      token = `temp_${Math.random().toString(36).substring(2)}_${Date.now()}`;
      writeScopedToken(token);
    }
    return token;
  }

  private handleMessage(data: ArrayBuffer): void {
    try {
      const { type, payload } = this.decodeMessage(data);

      switch (type) {
        case MessageType.Welcome:
          this.playerId = (payload as any).playerId;
          if ((payload as any).token) {
            writeScopedToken((payload as any).token);
          }
          console.log(`[Network] Connected as ${this.playerId}`);
          // Guardamos el Welcome por si llega antes de que React registre el
          // manejador (carrera en la carga inicial); se reproduce al registrarlo.
          this.pendingWelcome = payload;
          this.onWelcome?.(payload);
          break;
        case MessageType.Snapshot:
          this.onSnapshot?.(payload);
          break;
        case MessageType.MatchStart:
          console.log('[Network] Match started!');
          this.onMatchStart?.(payload);
          break;
        case MessageType.MatchEnd:
          console.log('[Network] Match ended');
          this.onMatchEnd?.(payload);
          break;
        case MessageType.MatchState:
          this.onMatchState?.(payload);
          break;
        case MessageType.QueueStatus:
          this.onQueueStatus?.(payload);
          break;
        case MessageType.PlayerCollision:
          this.onPlayerCollision?.(payload as PlayerCollisionEvent);
          break;
        case MessageType.UpgradeConfirm:
          this.onUpgradeResult?.(payload as UpgradePurchaseResult);
          break;
        case MessageType.MatchResultUpdated:
          this.onMatchResultUpdated?.(payload);
          break;
        case MessageType.WalletUpdated:
          this.onWalletUpdated?.(payload);
          break;
        case MessageType.Error:
          console.error(`[Network] Server error:`, payload);
          this.onError?.((payload as any).message);
          break;
      }
    } catch (err) {
      console.error('[Network] Error handling message:', err);
    }
  }

  private decodeMessage(buffer: ArrayBuffer): { type: MessageType; payload: any } {
    const view = new DataView(buffer);
    const type = view.getUint16(0, true) as MessageType;
    const parser = new TextDecoder();
    const json = parser.decode(buffer.slice(4));
    return { type, payload: JSON.parse(json) };
  }

  send(type: MessageType, payload: unknown): void {
    const encoder = new TextEncoder();
    const json = JSON.stringify(payload);
    const encoded = encoder.encode(json);

    const header = new ArrayBuffer(4);
    const headerView = new DataView(header);
    headerView.setUint16(0, type, true);
    headerView.setUint16(2, encoded.byteLength, true);

    const result = new ArrayBuffer(4 + encoded.byteLength);
    const resultView = new Uint8Array(result);
    resultView.set(new Uint8Array(header), 0);
    resultView.set(encoded, 4);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(result);
    } else {
      this.messageQueue.push(result);
    }
  }

  sendInput(input: PlayerInput): void {
    this.send(MessageType.Input, {
      sequence: input.sequence,
      timestamp: input.timestamp,
      dirX: input.dirX,
      dirY: input.dirY,
      inputMode: input.inputMode,
    });
  }

  sendUpgrade(request: UpgradePurchaseRequest): void {
    this.send(MessageType.Upgrade, request);
  }

  setPlayerName(name: string): void {
    const cleanName = name.trim().replace(/\s+/g, ' ').slice(0, 18);
    this.playerName = cleanName || 'Jugador';
  }

  getPlayerName(): string {
    return this.playerName;
  }

  joinQueue(): void {
    this.send(MessageType.JoinQueue, { name: this.playerName });
  }

  leaveQueue(): void {
    this.send(MessageType.LeaveQueue, {});
  }

  sendVoluntaryLeave(): void {
    this.send(MessageType.VoluntaryLeave, {});
  }

  requestQueueStatus(): void {
    this.send(MessageType.QueueStatus, {});
  }

  private flushQueue(): void {
    for (const msg of this.messageQueue) {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(msg);
      }
    }
    this.messageQueue = [];
  }

  onWelcomeEvent(callback: (data: any) => void): void {
    this.onWelcome = callback;
    // Se reproduce una sola vez: si el componente se re-registra en re-renders,
    // no volvemos a disparar peticiones (evita ERR_INSUFFICIENT_RESOURCES).
    if (callback && this.pendingWelcome) {
      const payload = this.pendingWelcome;
      this.pendingWelcome = null;
      callback(payload);
    }
  }
  onSnapshotEvent(callback: (data: any) => void): void { this.onSnapshot = callback; }
  onMatchStartEvent(callback: (data: any) => void): void { this.onMatchStart = callback; }
  onMatchEndEvent(callback: (data: any) => void): void { this.onMatchEnd = callback; }
  onQueueStatusEvent(callback: ((data: any) => void) | null): void { this.onQueueStatus = callback; }
  onMatchStateEvent(callback: (data: any) => void): void { this.onMatchState = callback; }
  onErrorEvent(callback: (error: string) => void): void { this.onError = callback; }
  onPlayerCollisionEvent(callback: (event: PlayerCollisionEvent) => void): void {
    this.onPlayerCollision = callback;
  }
  onUpgradeResultEvent(callback: (result: UpgradePurchaseResult) => void): void {
    this.onUpgradeResult = callback;
  }
  onMatchResultUpdatedEvent(callback: ((data: any) => void) | null): void {
    this.onMatchResultUpdated = callback;
  }
  onWalletUpdatedEvent(callback: ((data: any) => void) | null): void {
    this.onWalletUpdated = callback;
  }

  isConnected(): boolean { return this.connected; }
  getPlayerId(): string | null { return this.playerId; }

  disconnect(): void {
    this.connected = false;
    this.ws?.close();
    this.ws = null;
  }
}
